// GLSL for Waterfalls.tsx. Instanced (ShaderMaterial gets `instanceMatrix`
// automatically on an InstancedMesh). All motion is a function of `uTime`.

/** Falling sheet: a unit quad scaled to (width, height); uv.y 0 = base. */
export const SHEET_VERT = /* glsl */ `
  uniform float uTime;
  varying vec2 vUv;
  varying float vHeight;
  varying float vPhase;
  void main() {
    vUv = uv;
    // Instance scale.y is the fall height in world units.
    vHeight = length(instanceMatrix[1].xyz);
    vPhase = fract(sin(dot(instanceMatrix[3].xz, vec2(12.9898, 78.233))) * 43758.5453);
    vec3 p = position;
    // Subtle wobble along the facing normal (local z), stronger toward the base.
    float w = sin(uTime * 3.1 + position.y * 9.0 + vPhase * 6.28) * 0.025
            + sin(uTime * 5.3 + position.x * 11.0) * 0.012;
    p.z += w * (1.0 - uv.y * 0.6);
    gl_Position = projectionMatrix * modelViewMatrix * instanceMatrix * vec4(p, 1.0);
  }
`;

export const SHEET_FRAG = /* glsl */ `
  uniform float uTime;
  varying vec2 vUv;
  varying float vHeight;
  varying float vPhase;
  void main() {
    // Soft side fade so the sheet reads as a channel, not a rectangle.
    float side = smoothstep(0.0, 0.18, vUv.x) * smoothstep(1.0, 0.82, vUv.x);
    // Vertical streaks scrolling downward; frequency stretches with height.
    float x = vUv.x * 14.0 + vPhase * 10.0;
    float y = vUv.y * vHeight * 6.0 + uTime * 2.4;
    float streak = 0.5 + 0.5 * sin(x * 2.3 + sin(y * 1.7 + x) * 1.4);
    streak *= 0.6 + 0.4 * sin(y * 2.1 + x * 0.7 + vPhase * 6.28);
    // Foam: bright band at the lip (top) and the base.
    float lip = smoothstep(0.88, 1.0, vUv.y);
    float base = smoothstep(0.22, 0.0, vUv.y);
    float foamNoise = 0.6 + 0.4 * sin(vUv.x * 40.0 + uTime * 4.0 + vPhase * 9.0);
    float foam = clamp(lip + base * foamNoise, 0.0, 1.0);
    vec3 water = mix(vec3(0.22, 0.46, 0.62), vec3(0.62, 0.82, 0.92), streak);
    vec3 col = mix(water, vec3(0.95, 0.98, 1.0), foam);
    float alpha = (0.42 + 0.3 * streak + 0.35 * foam) * side;
    gl_FragColor = vec4(col, clamp(alpha, 0.0, 0.85));
  }
`;

/** Base mist/spray: camera-facing soft puff that breathes with uTime. */
export const MIST_VERT = /* glsl */ `
  uniform float uTime;
  varying vec2 vUv;
  varying float vPhase;
  void main() {
    vUv = uv;
    vPhase = fract(sin(dot(instanceMatrix[3].xz, vec2(12.9898, 78.233))) * 43758.5453);
    float size = length(instanceMatrix[0].xyz) * (1.0 + 0.12 * sin(uTime * 1.7 + vPhase * 6.28));
    vec4 centre = modelViewMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
    // Billboard: offset in view space.
    centre.xy += position.xy * size;
    gl_Position = projectionMatrix * centre;
  }
`;

export const MIST_FRAG = /* glsl */ `
  uniform float uTime;
  varying vec2 vUv;
  varying float vPhase;
  void main() {
    vec2 d = vUv - 0.5;
    float r = length(d) * 2.0;
    float soft = smoothstep(1.0, 0.1, r);
    float swirl = 0.75 + 0.25 * sin(atan(d.y, d.x) * 3.0 + uTime * 0.9 + vPhase * 6.28);
    gl_FragColor = vec4(0.88, 0.95, 1.0, soft * soft * swirl * 0.42);
  }
`;
